export interface NearbyActivityDefaultItem {
  id: string;
  name: string;
  type: string;
  duration: string;
  location: string;
  description: string;
  difficulty: string;
  imageUrl?: string;
  mapUrl?: string;
  lat?: number;
  lng?: number;
}

export const NEARBY_ACTIVITIES_DEFAULT_TITLE = 'Trekking Activities Nearby';
export const NEARBY_ACTIVITIES_DEFAULT_DESCRIPTION =
  'Trekking routes, hill viewpoints, and nature trails around Muchukunnu and Meppayur.';

export const NEARBY_ACTIVITIES_DEFAULTS: NearbyActivityDefaultItem[] = [
  {
    id: 'trk-1',
    name: 'Payamkuttimala Trek Trail',
    type: 'Trekking',
    duration: '2-3 hours',
    location: 'Payamkuttimala, Meppayur side',
    description:
      'Popular hill trek with open stretches, gradual climbs, and scenic countryside views.',
    difficulty: 'Moderate',
    imageUrl: 'https://picsum.photos/seed/payamkuttimala-trek/1200/800',
    lat: 11.5974125,
    lng: 75.6129219,
    mapUrl:
      'https://www.google.com/maps/search/?api=1&query=11.5974125,75.6129219',
  },
  {
    id: 'trk-2',
    name: 'Microwave Viewpoint Meppayur Trek',
    type: 'Viewpoint Trek',
    duration: '1.5-2 hours',
    location: 'Microwave Viewpoint, Meppayur',
    description:
      'Short-to-medium uphill trek to a panoramic viewpoint ideal for sunrise and sunset.',
    difficulty: 'Moderate',
    imageUrl: 'https://picsum.photos/seed/microwave-viewpoint-meppayur/1200/800',
    lat: 11.6761625,
    lng: 75.7375469,
    mapUrl:
      'https://www.google.com/maps/search/?api=1&query=11.6761625,75.7375469',
  }
];

interface NearbyActivityLike {
  id?: string;
  name?: string;
  type?: string;
  duration?: string;
  location?: string;
  description?: string;
  difficulty?: string;
  imageUrl?: string;
  mapUrl?: string;
  lat?: number | string;
  lng?: number | string;
}

const normalizeActivityName = (name: string) =>
  name.toLowerCase().replace(/\s+/g, ' ').trim();

const TREKKING_KEYWORDS = [
  'trek',
  'trekking',
  'hike',
  'hiking',
  'trail',
  'viewpoint',
  'ridge',
  'forest',
  'summit',
  'walk',
];

const NON_TREKKING_KEYWORDS = [
  'temple',
  'darshan',
  'pilgrim',
  'boating',
  'beach',
  'cruise',
  'cycling',
  'photography',
  'heritage',
  'cultural',
];

const isTrekkingLikeActivity = (item: NearbyActivityLike) => {
  const text = `${item.name || ''} ${item.type || ''} ${item.description || ''}`.toLowerCase();
  if (NON_TREKKING_KEYWORDS.some((keyword) => text.includes(keyword))) return false;
  return TREKKING_KEYWORDS.some((keyword) => text.includes(keyword));
};

const toOptionalNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const mergeWithNearbyActivityDefaults = (
  activities: NearbyActivityLike[] = []
): NearbyActivityDefaultItem[] => {
  const existing = Array.isArray(activities)
    ? activities.filter(
        (item): item is NearbyActivityLike =>
          !!item &&
          typeof item.name === 'string' &&
          item.name.trim().length > 0 &&
          isTrekkingLikeActivity(item)
      )
    : [];

  const defaultsByName = new Map(
    NEARBY_ACTIVITIES_DEFAULTS.map((item) => [normalizeActivityName(item.name), item] as const)
  );

  const normalizedExisting: NearbyActivityDefaultItem[] = existing.map((item, index) => {
    const normalizedName = normalizeActivityName(item.name || '');
    const fallback = defaultsByName.get(normalizedName);
    return {
      id: item.id || `activity-${index + 1}`,
      name: (item.name || fallback?.name || '').trim(),
      type: item.type || fallback?.type || 'Trekking',
      duration: item.duration || fallback?.duration || '',
      location: item.location || fallback?.location || '',
      description: item.description || fallback?.description || '',
      difficulty: item.difficulty || fallback?.difficulty || 'Easy',
      imageUrl: item.imageUrl || fallback?.imageUrl || '',
      mapUrl: item.mapUrl || fallback?.mapUrl || '',
      lat: toOptionalNumber(item.lat) ?? fallback?.lat,
      lng: toOptionalNumber(item.lng) ?? fallback?.lng,
    };
  });

  const seen = new Set(normalizedExisting.map((item) => normalizeActivityName(item.name)));
  const merged: NearbyActivityDefaultItem[] = [...normalizedExisting];

  NEARBY_ACTIVITIES_DEFAULTS.forEach((activity) => {
    const key = normalizeActivityName(activity.name);
    if (!seen.has(key)) {
      merged.push({ ...activity });
      seen.add(key);
    }
  });

  return merged;
};
